/**
 * ModelSelector — Shared model picker for AI workflows.
 *
 * Fetches available models from the backend, shows a dropdown when GitHub
 * is authenticated (multiple models available), or static text for the
 * default model. Handles fallback: if selectedModel isn't in the list,
 * picks the default or first available.
 *
 * Used by both CreateWorkflowDialog and ChatInput in the designer sidebar.
 */

import { useState, useEffect } from 'react';
import { Dropdown, Option, Text, makeStyles, tokens } from '@coreai/fluentui-react';
import { useGitHubAuthStore } from '@/stores/githubAuthStore';
import { useAgentChatAguiStore } from '@/stores/agentChatAguiStore';
import { useAppStore } from '@/stores/appStore';
import { useAuth } from '@/auth/useAuth';
import { getListModelsUrl } from '@/services/aguiStreamClient';
import { getWorkflowAssistantAffinityHeader, captureWorkflowAssistantAffinity } from '@/services/workflowAssistantAffinity';

export interface ModelInfo {
  id: string;
  displayName: string;
  isDefault: boolean;
}

const useStyles = makeStyles({
  dropdown: {
    minWidth: '140px',
    maxWidth: '200px',
  },
  label: {
    color: tokens.colorNeutralForeground3,
    whiteSpace: 'nowrap',
  },
});

interface ModelSelectorProps {
  /** Disable the dropdown (e.g., while streaming) */
  disabled?: boolean;
  /** Size variant */
  size?: 'small' | 'medium';
}

/** Loading status for the backend model catalog fetch. */
export type ModelCatalogStatus = 'idle' | 'loading' | 'loaded' | 'error';

/**
 * Fetches the backend model catalog (`listModels`) for the selected project.
 *
 * This is the single source of truth for "which models are actually
 * available" — it queries the live Copilot/BYOK catalog rather than any
 * hardcoded list. It has no side effects on the chat session: callers that
 * also need to sync the active selection use `useAvailableModels`, which
 * layers that behavior on top.
 */
export function useModelCatalog() {
  const [models, setModels] = useState<ModelInfo[]>([]);
  const [status, setStatus] = useState<ModelCatalogStatus>('idle');
  const isGitHubAuthenticated = useGitHubAuthStore((s) => s.isAuthenticated);
  const selectedProject = useAppStore((s) => s.selectedProject);
  const { getOttoToken } = useAuth();

  useEffect(() => {
    if (!selectedProject) return;
    let cancelled = false;
    setStatus('loading');
    getOttoToken().then((token) => {
      if (cancelled) return;
      if (!token) {
        setStatus('error');
        return;
      }
      fetch(
        getListModelsUrl(selectedProject),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            ...getWorkflowAssistantAffinityHeader(),
          },
        },
      )
        .then((res) => {
          captureWorkflowAssistantAffinity(res);
          return res.ok ? res.json() : Promise.reject(res.statusText);
        })
        .then((data: unknown) => {
          if (cancelled) return;
          if (!Array.isArray(data)) throw new Error('Invalid models response');
          setModels(data as ModelInfo[]);
          setStatus('loaded');
        })
        .catch((err) => {
          if (cancelled) return;
          console.warn('[ModelSelector] Failed to fetch models:', err);
          setModels([]);
          setStatus('error');
        });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGitHubAuthenticated, selectedProject]);

  return { models, status, isGitHubAuthenticated };
}

/**
 * Hook that fetches models and syncs the active chat `selectedModel` with the
 * available list. Shared between ModelSelector UI and any component that needs
 * the live model state for the current chat session.
 */
export function useAvailableModels() {
  const { models, status, isGitHubAuthenticated } = useModelCatalog();
  const selectedModel = useAgentChatAguiStore((s) => s.selectedModel);
  const setSelectedModel = useAgentChatAguiStore((s) => s.setSelectedModel);

  useEffect(() => {
    if (status === 'error') {
      setSelectedModel('');
      return;
    }
    if (status !== 'loaded' || models.length === 0) return;
    const normalize = (s: string) => s.replace(/[.\-_]/g, '').toLowerCase();
    if (models.some((m) => m.id === selectedModel)) return;
    const fuzzy = models.find((m) => normalize(m.id) === normalize(selectedModel));
    // Not in list — fall back to the catalog default or first entry.
    const next = fuzzy ?? models.find((m) => m.isDefault) ?? models[0];
    if (next) setSelectedModel(next.id);
  }, [status, models, selectedModel, setSelectedModel]);

  return { models, selectedModel, setSelectedModel, isGitHubAuthenticated };
}

export const ModelSelector = ({ disabled, size = 'small' }: ModelSelectorProps) => {
  const styles = useStyles();
  const { models, selectedModel, setSelectedModel, isGitHubAuthenticated } = useAvailableModels();

  // GitHub signed in + multiple models → dropdown
  if (isGitHubAuthenticated && models.length > 1) {
    return (
      <Dropdown
        className={styles.dropdown}
        size={size}
        value={models.find((m) => m.id === selectedModel)?.displayName ?? selectedModel}
        selectedOptions={[selectedModel]}
        onOptionSelect={(_, data) => {
          if (data.optionValue) setSelectedModel(data.optionValue);
        }}
        disabled={disabled}
        aria-label="Select AI model"
      >
        {models.map((m) => (
          <Option key={m.id} value={m.id}>
            {m.displayName}
          </Option>
        ))}
      </Dropdown>
    );
  }

  // Show model name — fallback to "Default model" if API hasn't loaded yet
  const displayModel = models.find((m) => m.isDefault) ?? models[0];
  const displayName = displayModel?.displayName ?? 'Default model';
  return (
    <Text size={200} className={styles.label} aria-label={`Model: ${displayName}`}>
      {displayName}
    </Text>
  );
};
